/**
 * `telegram_settings` — настройки Telegram-канала оповещений команды по
 * Авито-лидам.
 *
 * Источник правды: миграция `supabase/migrations/20260530120000_lead_telegram_notify.sql`
 * в репо `stryzh`. Таблица single-row (id=1). Read через RPC `get_telegram_settings`
 * (admin/dispatcher), write через RPC `update_telegram_settings(jsonb)` под admin.
 * Партиальное обновление: передавай только то, что меняешь.
 *
 * Токен бота тут НЕ хранится и не отдаётся — он в vault, доступен только
 * edge-функции send-telegram. В UI показываем только bot_username.
 *
 * Тестовое сообщение — edge `send-telegram` с body { test: true }.
 */
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

export interface TelegramSettings {
  id: number
  enabled: boolean
  chat_id: string | null
  bot_username: string | null
  event_new: boolean
  event_paid: boolean
  event_report: boolean
  event_refund: boolean
  event_cancelled: boolean
  event_stale: boolean
  stale_new_minutes: number
  stale_awaiting_minutes: number
  stale_new_repeat_minutes: number
  stale_work_start_hour: number
  stale_work_end_hour: number
  updated_at: string
  updated_by: string | null
}

/** Партиальное обновление — все поля опциональны. */
export type TelegramSettingsPatch = Partial<
  Omit<TelegramSettings, 'id' | 'bot_username' | 'updated_at' | 'updated_by'>
>

export async function getTelegramSettings(): Promise<TelegramSettings> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('get_telegram_settings')
  if (error) throw new Error(error.message)
  return data as TelegramSettings
}

export async function updateTelegramSettings(
  patch: TelegramSettingsPatch,
): Promise<TelegramSettings> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('update_telegram_settings', {
    p_patch: patch,
  })
  if (error) throw new Error(error.message)
  return data as TelegramSettings
}

/** Отправка тестового сообщения в настроенный чат (проверка связи). */
export async function sendTelegramTest(): Promise<void> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('send-telegram', {
    body: { test: true },
  })
  if (error) throw new Error(error.message)
  const res = data as { ok?: boolean; skipped?: string; reason?: string; description?: string }
  if (res?.skipped === 'disabled') {
    throw new Error(i18n.t('apiErr.tg.channelOff'))
  }
  if (!res?.ok) {
    throw new Error(res?.description || res?.reason || i18n.t('apiErr.tg.testFailed'))
  }
}

/** Описание событий для UI (порядок = порядок в форме). Метки/подсказки —
 *  i18n-backed через getter, чтобы пересчитывались при смене языка. */
export const TELEGRAM_EVENTS: ReadonlyArray<{
  key: keyof TelegramSettingsPatch
  label: string
  hint: string
}> = [
  { key: 'event_new', get label() { return i18n.t('settings.tgEvent.new.label') }, get hint() { return i18n.t('settings.tgEvent.new.hint') } },
  { key: 'event_paid', get label() { return i18n.t('settings.tgEvent.paid.label') }, get hint() { return i18n.t('settings.tgEvent.paid.hint') } },
  { key: 'event_report', get label() { return i18n.t('settings.tgEvent.report.label') }, get hint() { return i18n.t('settings.tgEvent.report.hint') } },
  { key: 'event_refund', get label() { return i18n.t('settings.tgEvent.refund.label') }, get hint() { return i18n.t('settings.tgEvent.refund.hint') } },
  { key: 'event_cancelled', get label() { return i18n.t('settings.tgEvent.cancelled.label') }, get hint() { return i18n.t('settings.tgEvent.cancelled.hint') } },
  { key: 'event_stale', get label() { return i18n.t('settings.tgEvent.stale.label') }, get hint() { return i18n.t('settings.tgEvent.stale.hint') } },
]
